/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["report", "originalReport", "options", "isMobile", "mobileReportingOptions", "onReportResolve", "trackingAssetType", "onError"];
import PropTypes from 'prop-types';
import { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { MOBILE_UNSUPPORTED } from 'reporting-data/constants/errorTypes';
import { has } from 'reporting-data/lib/has';
import customWidgets from 'reporting-ui-components/components/customWidgets';
import SnowflakeReport from 'reporting-ui-components/components/snowflake/SnowflakeReport';
import JourneyReportResolver from 'reporting-ui-components/components/journey/JourneyReport';
import CustomWidgetProps from 'reporting-ui-components/lib/CustomWidgetProps';
import ErrorBoundaryDecorator from 'reporting-ui-components/reports/decorators/ErrorBoundaryDecorator';
import ReportResolver from 'reporting-ui-components/reports/ReportResolver';
import styled from 'styled-components';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { MobileEnabledWidgets } from '../../constants/mobileEnabledWidgets';
import { getEditLink, getSandboxLink } from '../../lib/getEditLink';
import { getReportJson } from '../../lib/getReportJson';
import ErrorMessage from '../shared/ErrorMessage';
import { isImmutableJourneyReport } from 'reporting-snowflake/funnel/types/journeyReport';
import I18n from 'I18n';
import { EVENTS } from '../../lib/events';
import { getReportHoverUsageTrackerProperties } from '../../lib/usageTrackerUtils';
import { isUngatedForCustomWidgetMigration } from 'reporting-plugins/customWidgets/isUngatedForCustomWidgetMigration';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
import { getUsageTracker } from '../../monitoring/usageTracker';
import { Metrics } from '../../monitoring/Metrics';
import { REPORT_CONTENT_RESOLVE_TYPE, REPORT_CONTENT_RESOLVE_TYPES } from '../../constants/trackingMetrics';
import { getValidatedReportTrackingInfo } from '../PlatformReport/v2/unifiedVizValidationTrackingUtils';
import { UnifiedVizValidationContext } from 'reporting-ui-components/components/PlatformReport/v2/UnifiedVizValidationContextProvider';
import { hasReportingPlatformChromeExtension } from 'reporting-data/utils/reportingExtensionUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SeleniumWrapper = styled.div.withConfig({
  displayName: "ReportContent__SeleniumWrapper",
  componentId: "zveffj-0"
})(["display:contents;overflow:auto;max-height:100%;"]);
const REPORT_MINIMUM_HOVER_TIME = 1500;
const SnowflakeReportPromise = () => Promise.resolve(SnowflakeReport);
const JourneyReportPromise = () => Promise.resolve(JourneyReportResolver);
const resolverMapper = (report, maybeWidgetModule, userInfo) => {
  if (isRelationalReport(report)) {
    return SnowflakeReportPromise;
  }
  if (isImmutableJourneyReport(report)) {
    return JourneyReportPromise;
  }
  if (isUngatedForCustomWidgetMigration(maybeWidgetModule, userInfo)) {
    // Use ReportResolver using handlers set by reporting-plugins
    return undefined;
  }
  return customWidgets[maybeWidgetModule];
};
function ReportContent(_ref) {
  let {
      report,
      originalReport,
      options,
      isMobile,
      mobileReportingOptions,
      onReportResolve,
      trackingAssetType,
      onError
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const validatedReportTrackingInfo = getValidatedReportTrackingInfo(report);
  const {
    setLegacyReportResolveError
  } = useContext(UnifiedVizValidationContext);
  const reportContentOnError = useCallback(error => {
    try {
      setLegacyReportResolveError(error);
    } catch (e) {
      console.error('Error setting legacy report resolve error', e);
    }
    onError(error);
  }, [onError, setLegacyReportResolveError]);
  useEffect(() => {
    Metrics.counter(REPORT_CONTENT_RESOLVE_TYPE, {
      report_resolve_type: REPORT_CONTENT_RESOLVE_TYPES.STANDARD_RESOLVE,
      reportType: validatedReportTrackingInfo.reportType,
      chartType: validatedReportTrackingInfo.chartType
    }).increment();
  }, [validatedReportTrackingInfo.reportType, validatedReportTrackingInfo.chartType]);
  const [modules, setModules] = useState({});
  const widgetModule = report.getIn(['displayParams', 'customWidget', 'type']);
  const userInfo = useContext(UserInfoContext);
  const getResolver = resolverMapper(report, widgetModule, userInfo);
  const startHoverTime = useRef();
  const usageTracker = getUsageTracker();
  const isDrilldownReport = report.getIn(['displayParams', 'drilldown']) || false;
  useEffect(() => {
    if (!has(modules, widgetModule) && getResolver) {
      if (widgetModule && isMobile && !MobileEnabledWidgets.includes(widgetModule)) {
        setModules(Object.assign({}, modules, {
          [widgetModule]: () => /*#__PURE__*/_jsx(ErrorMessage, {
            type: MOBILE_UNSUPPORTED,
            isMobile: isMobile
          })
        }));
        onReportResolve();
      } else {
        getResolver().then(Component => setModules(Object.assign({}, modules, {
          [widgetModule]: Component
        })));
      }
    }
  }, [getResolver, widgetModule, modules, isMobile, onReportResolve]);
  const Content = getResolver ? modules[widgetModule] : ReportResolver;
  const sandboxReport = originalReport || report;
  const displayReportingEncoding = hasReportingPlatformChromeExtension();
  return Content ? /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(SeleniumWrapper, {
      "data-test-id": "report-loaded",
      className: "@@REPORTING__ENCODED_REPORT",
      "data-encoded-v2": displayReportingEncoding ? getEditLink(sandboxReport) : '',
      "data-sandbox-link-v2": displayReportingEncoding ? getSandboxLink() : '',
      "data-report-json-v2": displayReportingEncoding ? getReportJson(sandboxReport) : '',
      onMouseEnter: () => {
        startHoverTime.current = I18n.moment();
      },
      onMouseLeave: () => {
        const endHoverTime = I18n.moment();
        const totalHoverTime = startHoverTime.current && endHoverTime.diff(startHoverTime.current, 'milliseconds') || 0;
        if (totalHoverTime >= REPORT_MINIMUM_HOVER_TIME) {
          const hoverTrackerProperties = Object.assign({}, getReportHoverUsageTrackerProperties(report, trackingAssetType), {
            duration: totalHoverTime,
            isDrilldownReport
          });
          Metrics.timer(REPORT_MINIMUM_HOVER_TIME).update(totalHoverTime);
          usageTracker.track(EVENTS.hoverOver, hoverTrackerProperties);
        }
      },
      children: /*#__PURE__*/_jsx(Content, Object.assign({
        report: report,
        options: options,
        isMobile: isMobile,
        mobileReportingOptions: mobileReportingOptions,
        onReportResolve: onReportResolve,
        onError: reportContentOnError
      }, rest))
    })
  }) : /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
}
ReportContent.propTypes = Object.assign({}, CustomWidgetProps, {
  trackingAssetType: PropTypes.string,
  mobileReportingOptions: PropTypes.object
});
const WrappedReportContent = ErrorBoundaryDecorator(ReportContent);
export default WrappedReportContent;